import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import restaurantImg from "../assets/restorent/restaurant.png";
import {
  bestBbqRestaurantInPudukkottaiSchema,
  bestBbqRestaurantInPudukkottaiFaqSchema,
} from "../data/schemas";

const FAQS = [
  {
    q: "Which is the best BBQ restaurant in Pudukkottai?",
    a: "We're biased, so judge us by the plate. Barbequeen has been grilling for Tamil Nadu diners since 1999, and our Pudukkottai outlet serves the same double-marinated grill chicken and wood-fired biryani.",
  },
  {
    q: "Is Barbequeen a biryani restaurant in Pudukkottai or a BBQ place?",
    a: "Both. Biryani and grills are our two specialities, so we always suggest ordering one of each.",
  },
  {
    q: "What makes the best mutton biryani in Pudukkottai?",
    a: "Three things: mutton that comes apart under a spoon, rice that stays separate, and masala cooked into the meat rather than poured on top. Our one-pot dum biryani is built around all three.",
  },
  {
    q: "Do you have vegetarian options?",
    a: "Yes. Paneer dishes, veg meals, soups and Indian breads are on the menu, so vegetarians at the table aren't left out.",
  },
  {
    q: "What should I order on my first visit?",
    a: "Start with our double-marinated grill chicken and a chicken biryani. Add mutton biryani for bigger appetites and mandi for anyone who prefers milder food.",
  },
];

const OTHER_GUIDES = [
  {
    title: "Best Restaurant in Pudukkottai",
    path: "/blog/best-restaurant-in-pudukkottai",
    city: "Pudukkottai",
  },
  {
    title: "Best Biryani in Pudukkottai",
    path: "/blog/best-biryani-in-pudukkottai",
    city: "Pudukkottai",
  },
  {
    title: "Best Restaurant in Coimbatore",
    path: "/best-restaurant-in-coimbatore",
    city: "Coimbatore",
  },
  {
    title: "Best Restaurant in Salem",
    path: "/best-restaurant-in-salem",
    city: "Salem",
  },
  {
    title: "Best Restaurant in Erode",
    path: "/best-restaurant-in-erode",
    city: "Erode",
  },
  {
    title: "Best Restaurant in Krishnagiri",
    path: "/best-restaurant-in-krishnagiri",
    city: "Krishnagiri",
  },
];

const BestBbqRestaurantInPudukkottaiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const pageTitle =
    "Charcoal Smoke & Biryani Steam: Best BBQ & Biryani in Pudukkottai | Barbequeen";
  const canonicalUrl =
    "https://barbequeen.com/blog/best-bbq-restaurant-pudukkottai";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}
    >
      {/* ── SEO & Schemas ── */}
      <SEO
        title={pageTitle}
        metaTitle={pageTitle}
        description="Looking for the best BBQ restaurant and chicken or mutton biryani in Pudukkottai? Visit Barbequeen for double-marinated grill chicken & wood-fired dum biryani."
        keywords="best BBQ restaurant in Pudukkottai, best chicken biryani in Pudukkottai, best mutton biryani in Pudukkottai, Barbequeen Pudukkottai, double marinated chicken, wood fired biryani, family restaurant Pudukkottai"
        robots="index, follow"
        canonical={canonicalUrl}
        geo={{
          region: "IN-TN",
          placename: "Pudukkottai, Tamil Nadu",
          position: "10.3797;78.8208",
          icbm: "10.3797, 78.8208",
        }}
        og={{
          type: "article",
          url: canonicalUrl,
          title:
            "Charcoal Smoke and Biryani Steam: The Best BBQ Restaurant in Pudukkottai",
          description:
            "Discover double-marinated grill chicken and wood-fired Arcot-style biryani at Barbequeen Pudukkottai. Taste the difference on your first visit!",
          image:
            "https://barbequeen.com/assets/images/blog/best-bbq-biryani-pudukkottai.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
        schema={[
          bestBbqRestaurantInPudukkottaiSchema,
          bestBbqRestaurantInPudukkottaiFaqSchema,
        ]}
      />

      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "380px" }}
      >
        <img
          src="/assets/images/blog/best-bbq-biryani-pudukkottai.jpg"
          alt="Barbequeen Grill Chicken and Biryani in Pudukkottai"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          onError={(e) => {
            e.target.src = restaurantImg;
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.84) 0%, rgba(10,0,2,0.94) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-28 sm:pt-32 pb-14 text-left">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/60 mb-5">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="text-white/90 font-medium truncate max-w-[200px] sm:max-w-xs">
              Pudukkottai BBQ Guide
            </span>
          </nav>

          {/* Category Tag */}
          <span className="inline-block px-3 py-1 rounded-full text-white text-[10px] font-bold tracking-[0.2em] uppercase mb-4 bg-[#9b1c2e]">
            BBQ & Grill • Pudukkottai
          </span>

          {/* Title */}
          <h1 className="text-2xl sm:text-4xl lg:text-[40px] font-bold text-white leading-snug tracking-wide uppercase max-w-4xl mb-4">
            Charcoal Smoke and Biryani Steam: The Best BBQ Restaurant in Pudukkottai
          </h1>

          {/* Meta Bar */}
          <div className="flex flex-wrap items-center gap-5 text-xs text-white/80 font-sans pt-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#9b1c2e] flex items-center justify-center text-white font-bold text-xs">
                B
              </div>
              <span className="font-semibold text-white">
                Barbequeen Editorial
              </span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5 text-white/70">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>5 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Author / Social Share Row ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#9b1c2e] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
              B
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Barbequeen Editorial
              </p>
              <p className="text-xs text-gray-400">
                Serving Pudukkottai & Tamil Nadu Since 1999
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-white">
              Pudukkottai BBQ Guide
            </span>
            <span className="text-sm text-gray-500">SIPCOT Industrial Park</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">Share:</span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + canonicalUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-[#25d366] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              aria-label="Share on WhatsApp"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}&text=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              aria-label="Share on X (Twitter)"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-[#1877f2] flex items-center justify-center text-white hover:opacity-90 transition-opacity"
              aria-label="Share on Facebook"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Content + Sidebar Layout ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* ── Left Column: Article Body ── */}
          <main className="lg:col-span-8">
            {/* Intro Lead */}
            <p className="text-[16px] text-gray-700 leading-[1.85] mb-6 italic border-l-4 border-[#9b1c2e] pl-4 bg-white p-4 rounded-r-lg shadow-sm">
              Good barbecue is easy to recognise once you know what to look for.
              Before you trust any review, including ours, check these four
              things on the plate.
            </p>

            {/* Checklist Cards */}
            <div className="space-y-3.5 mb-8">
              {[
                {
                  title: "Marinade that goes beyond the surface",
                  desc: "Cut into the thickest piece. The flavour should carry into the meat, not sit only on the skin.",
                },
                {
                  title: "Char, not burn",
                  desc: "A good grill leaves dark, smoky edges. Black, bitter patches mean the meat was rushed over high heat.",
                },
                {
                  title: "Juice in every bite",
                  desc: "Well-grilled chicken stays moist near the bone. Dry, stringy meat usually means it was overcooked or left waiting too long.",
                },
                {
                  title: "Consistency",
                  desc: "A place worth recommending tastes the same on a busy Sunday as it does on a quiet Tuesday.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100 shadow-sm"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-[#9b1c2e] mt-2 flex-shrink-0" />
                  <p className="text-[15px] text-gray-700 leading-[1.8] flex-1">
                    <strong className="text-gray-900 font-semibold">
                      {item.title}.
                    </strong>{" "}
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8 bg-amber-50/70 p-4 rounded-xl border border-amber-200/60">
              Hygiene matters too. Clean tables and clean washrooms usually tell
              you how the kitchen behind them is run. With so many places to
              eat in Pudukkottai, these details are what turn a first visit into
              a habit.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Our Grill Menu: Double-Marinated Chicken, Tandoori and Mandi
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Our signature is <strong>double-marinated grill chicken</strong>.
              Double marination means the chicken is seasoned twice. The first
              marinade flavours the meat itself, and the second builds the
              spiced crust that chars on the grill.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              The{" "}
              <Link
                to="/blog/best-tandoori-restaurant-near-me"
                className="text-[#9b1c2e] font-semibold underline underline-offset-2 hover:text-[#7d1624] transition-colors"
              >
                tandoori
              </Link>{" "}
              and charcoal BBQ side of the menu covers the classics, from
              kebabs and chicken tikka to tandoori chicken. Tandoori dishes cook
              in the intense heat of a tandoor, which gives the edges a smoky
              char you can't get from a pan. Pair them with butter naan from our
              Indian breads section.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Want something milder? Our Arabian section serves mandi, the
              Yemeni-style dish of tender meat on fragrant, lightly spiced
              rice. It works well for kids and elders who find biryani masala
              too strong.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8">
              Shawarma, soups and Chinese dishes fill out the rest of the
              menu. Still, if you order only one thing, make it the grill
              chicken. Tear into the thickest piece and judge it by the
              checklist above.
            </p>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              A Biryani Restaurant in Pudukkottai That Still Cooks on Wood Fire
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Biryani in Tamil Nadu is personal. Most people judge a new
              biryani shop within three spoonfuls, usually against the one they
              grew up eating. So we stick to the slow way: dum cooking over wood
              fire, with seeraga samba rice.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8">
              Dum means the pot is sealed and cooked on gentle heat, so the
              rice finishes in the steam of the meat and masala. Seeraga samba,
              the small, fragrant grain behind classic Tamil Nadu biryani,
              soaks up that masala without turning mushy. Each plate comes with
              khatta gravy and onion raitha, the way biryani is meant to be eaten
              here.
            </p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Best Chicken Biryani in Pudukkottai: Arcot-Style and Wood-Fired
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8">
              Our chicken biryani follows a recipe tradition that goes back to
              the Nawabs of Arcot. When you taste it, look for three things:
              grains that stay separate, chicken that comes off the bone
              easily, and heat that builds slowly. Add a few pieces of grill
              chicken on the side, and you have the combination we'd order
              ourselves.
            </p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Best Mutton Biryani in Pudukkottai for Sunday Lunch
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8">
              Mutton biryani is the Sunday meal in many Pudukkottai homes, which
              makes it the hardest dish to impress anyone with. Ours is a dum
              biryani in which tender mutton, seeraga samba, mint, coriander
              and whole spices cook together in one pot. Properly cooked mutton
              should come apart under a spoon, with no chewy pieces to fight
              through.
            </p>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Best Restaurant in Pudukkottai for Families and Get-Togethers
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              A family restaurant has to please the whole table, from the
              cousin who wants the spiciest grill to the grandparent who eats
              only vegetarian. Our menu is built for that mix. Grills,
              tandoori and biryani sit alongside paneer dishes, Indian breads,
              soups and veg meals.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              That range matters when you're choosing the{" "}
              <strong>best restaurant in Pudukkottai</strong> for a birthday or
              an office dinner. We work just as well for a hungry stop after a
              day at Sittanavasal or Thirumayam Fort.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-8">
              Planning a bigger get-together? Call the outlet a day ahead to
              check seating for your group.
            </p>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Tips for Your First Visit
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              A few things we've learned from years of feeding hungry tables:
            </p>
            <div className="space-y-3 mb-8">
              {[
                {
                  title: "Order family-style",
                  desc: "Start with a couple of biryanis, a mix of grills and some breads, then add more once you see the portions.",
                },
                {
                  title: "Don't skip the sides",
                  desc: "The khatta and onion raitha balance rich biryani and smoky grills, so use them generously.",
                },
                {
                  title: "Match the dish to the diner",
                  desc: "Marinated meat can't be made milder after it's prepared, so steer mild eaters towards mandi or veg dishes.",
                },
                {
                  title: "Eat grills hot",
                  desc: "Grilled chicken loses its crisp edges in a closed takeaway box, so it's best eaten at the table.",
                },
                {
                  title: "Beat the rush",
                  desc: "Weekend meal times are the busiest, so arrive a little early if you want a relaxed meal.",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-[#9b1c2e] mt-2 flex-shrink-0" />
                  <p className="text-[15px] text-gray-700 leading-[1.8] flex-1">
                    <strong>{item.title}.</strong> {item.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Section 7: Visit Barbequeen Outlet */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 my-8 shadow-sm">
              <span className="text-[11px] font-bold tracking-[0.2em] text-[#9b1c2e] uppercase block mb-2">
                DINE-IN & TAKEAWAY
              </span>
              <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-3">
                Visit Barbequeen in Pudukkottai
              </h2>
              <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
                The <strong>best BBQ restaurant in Pudukkottai</strong> is the
                one that gets the basics right every time: proper marination,
                careful grilling and biryani cooked slowly. That's the standard
                we've held ourselves to for more than 25 years, and we'd love
                for you to test it.
              </p>
              <p className="text-[15px] text-gray-600 leading-[1.85] mb-6">
                Come in for lunch or dinner, bring the family, and decide for
                yourself who serves the{" "}
                <strong>best chicken biryani in Pudukkottai</strong>. Then tell
                us honestly what you think.
              </p>

              <div className="border-t border-gray-100 pt-5 space-y-3">
                <div className="flex items-start gap-3 text-sm text-gray-700">
                  <svg
                    className="w-5 h-5 text-[#9b1c2e] flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <div>
                    <strong className="block text-gray-900 font-semibold">
                      Barbequeen Restaurant - SIPCOT Industrial Park
                    </strong>
                    <span>
                      Plot No. 3 B/3, Door No. 1665, Nalan Food Products, SIPCOT
                      Industrial Park, Pudukkottai, Tamil Nadu 622002
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <svg
                    className="w-5 h-5 text-[#9b1c2e] flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <a
                    href="tel:+918675795788"
                    className="font-semibold text-gray-900 hover:text-[#9b1c2e] transition-colors"
                  >
                    +91 86757 95788
                  </a>
                </div>

                <div className="pt-2">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Barbequeen+Restaurant+SIPCOT+Industrial+Park+Pudukkottai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#9b1c2e] hover:text-[#7d1624] underline underline-offset-4"
                  >
                    View on Google Maps →
                  </a>
                </div>
              </div>
            </div>

            {/* Section 8: FAQs */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-12 mb-6 border-b border-gray-200 pb-2">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 font-semibold text-gray-800 text-sm hover:text-[#9b1c2e] transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span className="text-[#9b1c2e] text-lg font-bold flex-shrink-0">
                      {openFaq === idx ? "−" : "+"}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tags / Bottom Share */}
            <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span className="font-semibold text-gray-700">Tags:</span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Best BBQ restaurant in Pudukkottai
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Best chicken biryani in Pudukkottai
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Best mutton biryani in Pudukkottai
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  SIPCOT BBQ
                </span>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + canonicalUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#25d366] text-white text-xs font-bold rounded-lg hover:opacity-90 transition-opacity"
              >
                Share on WhatsApp
              </a>
            </div>
          </main>

          {/* ── Right Column: Sidebar ── */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Quick Actions Widget */}
            <div className="bg-[#1a0a0d] text-white rounded-2xl p-6 shadow-md">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#c0424e] uppercase block mb-2">
                SINCE 1999
              </span>
              <h3 className="text-lg font-bold mb-3">
                Visit Barbequeen Pudukkottai
              </h3>
              <p className="text-xs text-gray-300 leading-relaxed mb-5">
                Taste the charcoal grill legend at SIPCOT Industrial Park with
                our signature double-marinated chicken, wood-fired Arcot-style
                seeraga samba dum biryani, or Arabian chicken mandi.
              </p>
              <div className="space-y-2.5">
                <Link
                  to="/menu"
                  className="block w-full py-2.5 text-center bg-[#9b1c2e] hover:bg-[#801423] text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Explore Full Menu
                </Link>
                <a
                  href="tel:+918675795788"
                  className="block w-full py-2.5 text-center bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Call +91 86757 95788
                </a>
                <Link
                  to="/contact-us"
                  className="block w-full py-2.5 text-center bg-transparent hover:bg-white/5 border border-white/10 text-gray-300 text-xs font-semibold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Contact & Directions
                </Link>
              </div>
            </div>

            {/* Other Guides Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#9b1c2e]"></span>
                Related Guides
              </h3>
              <div className="space-y-3">
                {OTHER_GUIDES.map((guide, idx) => (
                  <Link
                    key={idx}
                    to={guide.path}
                    className="group block p-3 rounded-xl bg-gray-50 hover:bg-[#9b1c2e]/5 border border-transparent hover:border-[#9b1c2e]/20 transition-all duration-200"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-[#9b1c2e] block mb-0.5">
                      {guide.city}
                    </span>
                    <p className="text-sm font-semibold text-gray-800 group-hover:text-[#9b1c2e] transition-colors">
                      {guide.title} →
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Why Barbequeen Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                Why Diners Choose Us
              </h3>
              <ul className="space-y-3 text-xs text-gray-600">
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Double Marinated:</strong> Signature spice blend
                    infused deep into the meat before charring.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Real Charcoal & Wood Fire:</strong> Traditional
                    authentic grilling & dum cooking.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>25+ Years Legacy:</strong> Serving families with
                    consistent taste since 1999.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>SIPCOT Location:</strong> Convenient dining and
                    takeaway hub in Pudukkottai.
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BestBbqRestaurantInPudukkottaiPage;
